import { useMemo } from 'react';
import { createColumnHelper, useReactTable, getCoreRowModel } from '@tanstack/react-table';

import {
  adjustMinimalClassTimeAcrossTZ,
  CoachAttendanceStatus,
  GetCoachAttendanceRollupResult,
  StudentGroupAuditWithClassTimeLevel,
} from '@airreading/common/data';
import { formatPlainDate, FrequentTimeZones } from '@airreading/common';
import { formatCoachAttendanceRollup, formatStudentGrades } from '@/lib/student-group-utils';

import { EntityTable } from '@/components/table/entity-table';
import { InfoCard } from '@/components/info-card';
import { Badge } from '@/components/ui/badge';
import { PeopleBadgeList } from '../../scheduling/student-groups/-components/people-badge-list';
import { formatStudentClassTimeAbbr } from '../../scheduling/schedules/-components/formatting';
import { CountAsReassignedToggle } from './toggle-count-as-reassigned';
import { PaginationRequest, usePagination } from '@/lib/pagination';
import { RegisteredRouteTree, RoutesWithValidConstraint } from '@/lib/routing';
import { RouteById } from '@tanstack/react-router';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { EmptyArray, getCoachAttendanceRollupsForGroups, getCoachReassignedStudentGroups } from '@/lib/api-calls';
import { TableErrorState } from '@/components/table/table-error-state';

const columnHelper = createColumnHelper<StudentGroupAuditWithClassTimeLevel>();
const getColumns = (attendanceMap: Record<string, GetCoachAttendanceRollupResult>) => [
  columnHelper.accessor((row) => row.studentGroup.classTime, {
    id: 'classTimes',
    header: 'Class times (CT)',
    enableSorting: false,
    cell: ({ getValue }) => {
      const classTime = getValue();
      return (
        <Badge variant="outline" className="bg-[#F1F1F1] font-normal">
          {formatStudentClassTimeAbbr(
            adjustMinimalClassTimeAcrossTZ(
              classTime,
              classTime.schedule.school.org.timeZone,
              FrequentTimeZones.UsCentral,
            ),
          )}
        </Badge>
      );
    },
  }),
  columnHelper.accessor('coachAssignedAtPlainDate', {
    id: 'startDate',
    header: 'Start date',
    enableSorting: false,
    cell: ({ getValue }) => {
      const value = getValue();
      return value ? formatPlainDate(value) : '-';
    },
  }),
  columnHelper.accessor('coachUnassignedAtPlainDate', {
    id: 'endDate',
    header: 'End date',
    enableSorting: false,
    cell: ({ getValue }) => {
      const value = getValue();
      return value ? formatPlainDate(value) : '-';
    },
  }),
  columnHelper.accessor((row) => row.studentGroup.students, {
    id: 'students',
    header: 'Students',
    enableSorting: false,
    cell: ({ getValue }) => {
      return <PeopleBadgeList people={getValue()} />;
    },
  }),
  columnHelper.accessor((row) => row.studentGroup.grades, {
    id: 'grades',
    header: 'Grades',
    enableSorting: false,
    cell: ({ getValue }) => formatStudentGrades(getValue()),
  }),
  columnHelper.accessor((row) => row.studentGroup.classTime?.schedule?.school?.name ?? '', {
    id: 'school',
    header: 'School',
    enableSorting: false,
    cell: ({ getValue }) => getValue(),
  }),
  columnHelper.accessor((row) => row.studentGroup.classTime?.schedule?.school?.org?.name ?? '', {
    id: 'district',
    header: 'District',
    enableSorting: false,
    cell: ({ getValue }) => getValue(),
  }),
  columnHelper.accessor((row) => row, {
    id: 'reassigned',
    header: 'Count as reassigned',
    enableSorting: false,
    cell: ({ getValue }) => <CountAsReassignedToggle studentGroupAudit={getValue()} />,
  }),
  columnHelper.accessor((row) => row.studentGroup, {
    id: 'absences',
    header: 'Teacher absences',
    enableSorting: false,
    cell: ({ getValue }) => {
      const { id, maxPlannedAbsences } = getValue();
      const coachAttendanceRollup = attendanceMap[id];

      if (
        !coachAttendanceRollup ||
        coachAttendanceRollup.status === CoachAttendanceStatus.Perfect ||
        coachAttendanceRollup.items.length === 0
      ) {
        return '-';
      }

      const categories = formatCoachAttendanceRollup(coachAttendanceRollup);
      if (!categories) {
        return '-';
      }

      return (
        <div className="flex items-center gap-2">
          <div className="flex flex-col gap-1">
            {Object.keys(categories).map(
              (category) =>
                categories[category].dates.length > 0 && (
                  <div key={category}>
                    {category}
                    {categories[category].detail?.(categories[category].dates.length, maxPlannedAbsences)}:{' '}
                    {categories[category].dates.join(', ')}
                  </div>
                ),
            )}
          </div>
        </div>
      );
    },
  }),
];

export function ReassignedStudentGroupsCard({
  coachId,
  route,
}: {
  coachId: string;
  route: RouteById<RegisteredRouteTree, RoutesWithValidConstraint<PaginationRequest>>;
}) {
  const { pageIndex, pageSize, onPaginationChange } = usePagination(route.id);
  const {
    data: response,
    error,
    refetch,
    isPending,
  } = useQuery(getCoachReassignedStudentGroups(coachId, { pageIndex, pageSize }));
  const reassignedStudentGroups = useMemo(() => response?.reassignedStudentGroups || EmptyArray, [response]);
  const totalRowCount = useMemo(() => response?.total || 0, [response]);

  const studentGroupIds = reassignedStudentGroups.map((c) => c.studentGroupId);
  const { data: attendanceMap = {} } = useSuspenseQuery(
    getCoachAttendanceRollupsForGroups({ coachId, studentGroupIds }),
  );

  const columns = useMemo(() => getColumns(attendanceMap), [attendanceMap]);

  const table = useReactTable({
    data: reassignedStudentGroups,
    columns,
    state: {
      pagination: {
        pageIndex,
        pageSize,
      },
    },
    getCoreRowModel: getCoreRowModel(),
    onPaginationChange,
    manualPagination: true,
    rowCount: totalRowCount,
  });

  return (
    <InfoCard headerText="Past groups">
      <EntityTable
        table={table}
        hasError={!!error}
        showPagination
        isLoading={isPending}
        errorState={<TableErrorState refetchAction={refetch} />}
        totalRowCount={totalRowCount}
        emptyStateClassName="translate-y-0"
      />
    </InfoCard>
  );
}
